import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import settings from 'settings';
import _ from 'lodash';
import { connect } from 'react-redux';
import {
  assignUserToPlaceHolder,
  loadActivityTemplate,
  removeAssignedUserFromPlaceHolder,
  reorderAssignedUser,
  swapUserWithPlaceholder
} from 'actions/templateMapperActions';
import { addBulkActivitiesToTimeline, checkAddedActivities, removeActivitiesInAssignmentRange } from 'actions/rosterActions';
import { assignTemplateItemsToUsers, TIMELINE_DAYS_TO_SHOW, TIMELINE_START_DATE } from 'selectors/timeline';
import HeaderBar from 'components/headerBar/header-bar';
import ToolBar from 'components/toolBar/tool-bar';
import { GroupRenderer, TemplateItemRenderer } from 'components/Timeline';
import SplitPane from 'react-split-pane';
import { TIMEBAR_FORMAT } from 'views/Roster/timeline/TemplateTimeline';
import {
  Button, Checkbox, Icon, Modal, Spin, Tooltip
} from 'antd';
import Timeline from '../../../../../libs/timeline/timeline';
import TemplateMapperUserList from './template-mapper-user-list';

const SLIDE_PANEL_MIN_SIZE = settings.APP_CONFIG.slidePanelVerticalMinSize;
const SLIDE_PANEL_MAX_SIZE = settings.APP_CONFIG.slidePanelVerticalMaxSize;

const { TIMELINE_MODES } = Timeline;
const DEFAULT_TIMELINE_VISIBLE_DAYS = 7;
const DRAG_TYPE = {
  ASSIGN_USER: 'ASSIGN_USER',
  REORDER_USER: 'REORDER_USER'
};

const handleUserListDragStart = (e, user) => {
  e.dataTransfer.setData('data', JSON.stringify({
    type: DRAG_TYPE.ASSIGN_USER,
    user
  }));
};

const handleGroupDragStart = (e, group) => {
  e.dataTransfer.setData('data', JSON.stringify({
    type: DRAG_TYPE.REORDER_USER,
    user: group
  }));
};

const canDestructiveEdit = (timelineItems, rosterDateRange, insertTime, isRelative) => {
  const timelineStartDateOffset = moment(TIMELINE_START_DATE);
  const rosterEndTime = rosterDateRange[1];
  let canDescruct = true;
  let minEnd = null;
  if (!isRelative) {
    insertTime.startOf('day');
  }

  for (let i = 0; i < timelineItems.length; i++) {
    const item = timelineItems[i];
    const itemRosterEndTime = insertTime.clone()
      .add(item.end_time.valueOf() - timelineStartDateOffset.valueOf());

    if (itemRosterEndTime.isAfter(rosterEndTime)) {
      canDescruct = false;
      if (minEnd === null || itemRosterEndTime.isAfter(minEnd)) {
        minEnd = itemRosterEndTime;
      }
    }
  }
  const minEndTxt = minEnd === null ? '' : minEnd.format('LL');
  return {
    can: canDescruct,
    minEnd: minEndTxt
  };
};

class TemplateMapper extends React.Component {
  constructor(props) {
    super(props);

    this.MIN_VISIBLE_DATE = moment(TIMELINE_START_DATE);
    this.MAX_VISIBLE_DATE = moment(this.MIN_VISIBLE_DATE)
      .add(TIMELINE_DAYS_TO_SHOW, 'day');

    this.state = {
      visibleTimeStart: this.MIN_VISIBLE_DATE,
      visibleTimeEnd: moment(this.MIN_VISIBLE_DATE)
        .add(DEFAULT_TIMELINE_VISIBLE_DAYS, 'day'),

      isRelative: false,
      isDestructive: false,
      destructiveEdit: {
        can: false,
        minEnd: ''
      },
      isUnallocated: true
    };

    this.handlePanBack = this.handlePanBack.bind(this);
    this.handlePanForward = this.handlePanForward.bind(this);

    this.handleGroupDragDrop = this.handleGroupDragDrop.bind(this);
    this.handleGroupDelete = this.handleGroupDelete.bind(this);
    this.renderGroupTitle = this.renderGroupTitle.bind(this);
    this.toggleRelative = this.toggleRelative.bind(this);
  }

  componentDidMount() {
    const { templateId, loadTemplate } = this.props;
    if (templateId) {
      loadTemplate(templateId);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { items, rosterDateRange, rosterEventDetail } = nextProps;
    const { isRelative } = this.state;
    const destructiveEdit = canDestructiveEdit(items, rosterDateRange, rosterEventDetail.insertTime.clone(), isRelative);

    this.setState({ destructiveEdit });
  }

  applyTemplate() {
    const {
      rosterEventDetail, checkCompetencies, dispatchAddBulkActivitiesToTimeline, dispatchRemoveActivitiesInAssignmentRange, items, groups
    } = this.props;
    const { isRelative, isDestructive, isUnallocated } = this.state;

    const getCheckDate = (date) => {
      const timelineStartDateOffset = moment(TIMELINE_START_DATE);
      if (isRelative) {
        const adjustedDate = date.clone()
          .add(rosterEventDetail.insertTime.valueOf() - timelineStartDateOffset.valueOf(), 'milliseconds');
        return adjustedDate.format('YYYY-MM-DD HH:mm:ss');
      }

      const adjustedDate = date.clone()
        .startOf('day')
        .add(rosterEventDetail.insertTime.valueOf() - timelineStartDateOffset.valueOf(), 'milliseconds');
      return adjustedDate.format('YYYY-MM-DD HH:mm:ss');
    };

    const applyFunc = () => {
      const assignedActivities = assignTemplateItemsToUsers([...items], [...groups], rosterEventDetail.insertTime, isRelative, isUnallocated);
      if (isDestructive) {
        dispatchRemoveActivitiesInAssignmentRange(assignedActivities);
      }
      dispatchAddBulkActivitiesToTimeline(assignedActivities);
    };

    const itemsToCheck = items.map((i) => {
      const group = _.find(groups, { id: i.group });
      const userId = group && group.assignedUser ? group.assignedUser.uid : null;
      return {
        user_id: userId,
        activity_id: i.activity.id,
        check_datetime: getCheckDate(i.end_time)
      };
    })
      .filter(i => i.user_id !== null);

    if (itemsToCheck.length === 0) {
      return new Promise((resolve) => {
        applyFunc();
        resolve(true);
      });
    }

    return checkCompetencies(itemsToCheck)
      .then((r) => {
        if (r.length > 0) {
          return new Promise((resolve) => {
            const itemsToDisplay = _.uniqWith(r, _.isEqual);
            Modal.confirm({
              title: 'Competency Warnings',
              content: (
                <div>
                  <p>
                    The following users do not have the required competencies for their assigned activities:
                  </p>
                  <div style={{
                    overflowY: 'auto',
                    maxHeight: '100px'
                  }}
                  >
                    {itemsToDisplay.map(i => (
                      <div key={`${i.id}-${i.user_id}-${i.activity_id}`}>
                        <span>
                          <b>{i.user_name}</b>
                          {' '}
                          requires
                          <b>{i.name}</b>
                          {' '}
                          for
                          <b>{i.activity_name}</b>
                        </span>
                        <br />
                      </div>
                    ))}
                  </div>
                </div>
              ),
              okText: 'Ignore competencies and apply template',
              cancelText: 'Cancel',
              onOk() {
                applyFunc();
                resolve(true);
              },
              onCancel() {
                resolve(false);
              },
              width: '700px'
            });
          });
        }

        applyFunc();
        return true;
      });
  }

  assignUser(group, user) {
    const { dispatchAssignUserToPlaceHolder } = this.props;

    const newUser = {
      id: group.id,
      uid: user.id,
      title: `${user.first_name} ${user.last_name}`,
      active: user.is_active
    };
    dispatchAssignUserToPlaceHolder(newUser, group.id);
  }

  reorderUser(group, user) {
    const { dispatchReorderAssignedUser, dispatchSwapUserWithPlaceholder } = this.props;

    // If both the dragStart and dragEnd users are assigned then perform a reorder.
    // Otherwise a placeholder user is involved and we merely trade places
    //
    if (!!group.assignedUser && !!user.assignedUser) {
      dispatchReorderAssignedUser(user.id, group.id);
    }
    else {
      dispatchSwapUserWithPlaceholder(user.id, group.id);
    }
  }

  handlePanForward() {
    const { visibleTimeStart, visibleTimeEnd } = this.state;
    const diffDays = visibleTimeEnd.diff(visibleTimeStart, 'days');
    const endDate = moment.min(moment(visibleTimeEnd)
      .add(diffDays, 'days'), this.MAX_VISIBLE_DATE);

    this.setState({
      visibleTimeStart: moment(endDate)
        .add(-diffDays, 'days'),
      visibleTimeEnd: endDate
    });
  }

  handlePanBack() {
    const { visibleTimeStart, visibleTimeEnd } = this.state;
    const diffDays = visibleTimeEnd.diff(visibleTimeStart, 'days');
    const startDate = moment.max(moment(visibleTimeStart)
      .add(-diffDays, 'days'), this.MIN_VISIBLE_DATE);

    this.setState(() => ({
      visibleTimeStart: startDate,
      visibleTimeEnd: moment(startDate)
        .add(diffDays, 'days')
    }));
  }

  handleGroupDragDrop(e, group) {
    const data = JSON.parse(e.dataTransfer.getData('data'));
    const { type, user } = data;

    switch (type) {
    case DRAG_TYPE.ASSIGN_USER:
      this.assignUser(group, user);
      break;

    case DRAG_TYPE.REORDER_USER:
      this.reorderUser(group, user);
      break;

    default:
      break;
    }
  }

  handleGroupDelete(group) {
    const { dispatchRemoveAssignedUserFromPlaceHolder } = this.props;
    dispatchRemoveAssignedUserFromPlaceHolder(group.id);
  }

  toggleRelative() {
    const { items, rosterDateRange, rosterEventDetail } = this.props;
    const { isRelative } = this.state;
    const destructiveEdit = canDestructiveEdit(items, rosterDateRange, rosterEventDetail.insertTime.clone(), isRelative);
    this.setState({
      isRelative: !isRelative,
      destructiveEdit
    });
  }

  renderGroupTitle() {
    const { groups } = this.props;
    const title = `${groups.length} User${groups.length === 1 ? '' : 's'}`;

    return (
      <div className="bhp-group-title">
        {title}
      </div>
    );
  }

  render() {
    const {
      items, groups, template, rosterEventDetail, loading
    } = this.props;
    const {
      visibleTimeStart, visibleTimeEnd, isRelative, isDestructive, destructiveEdit, isUnallocated
    } = this.state;
    const selectedDate = moment(rosterEventDetail.insertTime)
      .format('LLL');

    const disablePanBack = visibleTimeStart.valueOf() === this.MIN_VISIBLE_DATE.valueOf();
    const disablePanForward = visibleTimeEnd.valueOf() === this.MAX_VISIBLE_DATE.valueOf();

    const timelineItems = items.map(i => ({
      title: i.title,
      key: i.id,
      row: i.group,
      start: moment(i.start_time),
      end: moment(i.end_time),
      color: i.color,
      activity: i.activity
    }));

    let destructiveToolTipTxt = 'If checked, activities in the template range for the selected users will be marked for deletion.';
    if (!destructiveEdit.can) {
      destructiveToolTipTxt = `To use destructive editing on this template zoom out on the roster such that ${destructiveEdit.minEnd} is visible`;
    }

    const filteredRosterUserCount = Object.keys(
      _.chain(groups)
        .filter(g => g.assignedUser !== undefined)
        .groupBy('assignedUser.uid')
        .value()
    ).length;
    const templateUserCount = groups.length;
    const showTemplateUserCountWarning = filteredRosterUserCount < templateUserCount;
    return (
      <div className="bhp-template-mapper bhp-container">
        <HeaderBar title={`Template: ${template.name}`} />
        <SplitPane split="vertical" defaultSize="35%" minSize={SLIDE_PANEL_MIN_SIZE} maxSize={SLIDE_PANEL_MAX_SIZE}>
          <TemplateMapperUserList onDragStart={handleUserListDragStart} />
          <Spin indicator={<Icon type="loading" spin />} tip="Loading..." spinning={loading}>
            <ToolBar>
              <ToolBar.Left>
                <Tooltip
                  placement="bottom"
                  title="Pan back"
                >
                  <Button size="small" icon="left" onClick={this.handlePanBack} disabled={disablePanBack} />
                </Tooltip>
                <Tooltip placement="bottom" title="Starting date where the template will be inserted in the roster.">
                  <div className="bhp-tag">{`Selected Date: ${selectedDate}`}</div>
                </Tooltip>
                <Tooltip
                  placement="bottom"
                  title="Pan forward"
                >
                  <Button size="small" icon="right" onClick={this.handlePanForward} disabled={disablePanForward} />
                </Tooltip>
                {showTemplateUserCountWarning
                && (
                  <Tooltip
                    placement="bottom"
                    title="This template contains more users than are visible with your current filtering options"
                  >
                    <Button type="dashed" style={{ cursor: 'default' }} size="small" icon="warning" />
                  </Tooltip>
                )}
              </ToolBar.Left>
              <ToolBar.Right>
                <Tooltip
                  placement="bottom"
                  title="If checked, activities with no user go to unallocated. If not checked, these activities are discarded"
                >
                  <Checkbox checked={isUnallocated} onChange={() => this.setState({ isUnallocated: !isUnallocated })}>
                    Unallocate unmapped activities
                  </Checkbox>
                </Tooltip>
                <Tooltip
                  placement="bottom"
                  title={destructiveToolTipTxt}
                >
                  <Checkbox disabled={!destructiveEdit.can} checked={isDestructive} onChange={() => this.setState({ isDestructive: !isDestructive })}>
                    Overwrite existing activities
                  </Checkbox>
                </Tooltip>
                <Tooltip
                  placement="bottomRight"
                  title="Activities will be imported with the times shown below, as opposed to being imported relative to the selected time."
                >
                  <Checkbox checked={!isRelative} onChange={this.toggleRelative}>
                    Preserve template times
                  </Checkbox>
                </Tooltip>
              </ToolBar.Right>
            </ToolBar>
            <div className="bhp-timeline">
              {!loading && (
                <Timeline
                  shallowUpdateCheck
                  showCursorTime
                  cursorTimeFormat="[Day] DDD - HH:mm"
                  groups={groups}
                  groupTitleRenderer={this.renderGroupTitle}
                  groupOffset={200}
                  groupRenderer={({ group }) => {
                    const rowIndex = group.id + 1;
                    const title = `${group.title} ${rowIndex} ${group.assignedUser ? `- ${group.assignedUser.title}` : ''}`;
                    return (
                      <GroupRenderer
                        isUser
                        rowIndex={rowIndex.toString()}
                        title={title}
                        data={group}
                        isClickable={false}
                        isDraggable
                        onDragStart={handleGroupDragStart}
                        onDragDrop={this.handleGroupDragDrop}
                        onDelete={this.handleGroupDelete}
                        canHover={false}
                      />
                    );
                  }}
                  items={timelineItems}
                  itemRenderer={({ ...props }) => (
                    <TemplateItemRenderer
                      {...props}
                      showContextMenu={false}
                    />
                  )}
                  selectedItems={[]}
                  componentId="tmap"
                  timelineMode={TIMELINE_MODES.SELECT}
                  timebarFormat={TIMEBAR_FORMAT}
                  startDate={visibleTimeStart}
                  endDate={visibleTimeEnd}
                  onInteraction={() => {
                  }}
                />
              )}
            </div>
          </Spin>
        </SplitPane>
      </div>
    );
  }
}

TemplateMapper.propTypes = {
  // Reducer Dispatch
  loadTemplate: PropTypes.func.isRequired,
  checkCompetencies: PropTypes.func.isRequired,
  dispatchAddBulkActivitiesToTimeline: PropTypes.func.isRequired,
  dispatchRemoveActivitiesInAssignmentRange: PropTypes.func.isRequired,
  dispatchAssignUserToPlaceHolder: PropTypes.func.isRequired,
  dispatchReorderAssignedUser: PropTypes.func.isRequired,
  dispatchSwapUserWithPlaceholder: PropTypes.func.isRequired,
  dispatchRemoveAssignedUserFromPlaceHolder: PropTypes.func.isRequired,
  // Other
  templateId: PropTypes.number.isRequired,
  rosterDateRange: PropTypes.arrayOf(PropTypes.object).isRequired,
  template: PropTypes.object.isRequired,
  rosterEventDetail: PropTypes.object.isRequired,
  items: PropTypes.array,
  groups: PropTypes.array,
  loading: PropTypes.bool
};

TemplateMapper.defaultProps = {
  items: [],
  groups: [],
  loading: false
};

const mapStateToProps = state => ({
  template: state.templateMapper.template,
  rosterEventDetail: state.templateMapper.rosterEventDetail,
  loading: state.templateMapper.loading,
  groups: state.templateMapper.groups,
  items: state.templateMapper.items
});

const mapDispatchToProps = dispatch => ({
  checkCompetencies(itemsToCheck) {
    return dispatch(checkAddedActivities(itemsToCheck));
  },
  loadTemplate(templateId) {
    dispatch(loadActivityTemplate(templateId));
  },
  dispatchAssignUserToPlaceHolder(user, placeHolderGroupId) {
    dispatch(assignUserToPlaceHolder(user, placeHolderGroupId));
  },
  dispatchRemoveAssignedUserFromPlaceHolder(placeHolderGroupId) {
    dispatch(removeAssignedUserFromPlaceHolder(placeHolderGroupId));
  },
  dispatchReorderAssignedUser(fromGroupId, toGroupId) {
    dispatch(reorderAssignedUser(fromGroupId, toGroupId));
  },
  dispatchSwapUserWithPlaceholder(fromGroupId, toGroupId) {
    dispatch(swapUserWithPlaceholder(fromGroupId, toGroupId));
  },

  dispatchAddBulkActivitiesToTimeline(activities = []) {
    dispatch(addBulkActivitiesToTimeline(activities));
  },
  dispatchRemoveActivitiesInAssignmentRange(activities = []) {
    dispatch(removeActivitiesInAssignmentRange(activities));
  }
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  null,
  { withRef: true }
)(TemplateMapper);
